import { User } from '@datadog/browser-core';
import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';

import env from 'utils/env';
import { PIIRedactor } from './utils';

interface UserContext extends User {
  orgId: number | null;
  companyName: string;
  projectId: number;
  projectName: string;
}

type TeamContext = {
  name: string;
  product: string;
};

// making sure that the metrics generated from cypress tests going into the staging env, to keep DD for prod clean
const app = window.location.hostname.includes('localhost')
  ? 'iterable-frontend-staging'
  : process.env.DD_RUM_APP;
const deployEnv = process.env.DD_RUM_ENV;

const RumEnvConfig: Record<string, RumInitConfiguration> = {
  'iterable-frontend': {
    service: 'iterable-frontend',
    applicationId: 'f024bfc2-0440-49cf-a3ec-18388de90cd5',
    clientToken: 'pub5bb46b054e9c10daf50cc8b771a4d42a',
    env: deployEnv,
    allowedTracingUrls: [
      (url) => url.startsWith('https://app.iterable.com'),
      (url) => url.startsWith('https://app.eu.iterable.com'),
    ],
  },
  'iterable-frontend-staging': {
    service: 'iterable-frontend-staging',
    applicationId: 'dc821882-9e82-4ef0-9d37-eb6624cea051',
    clientToken: 'pub41404cfd79da01750e87f416967d589c',
    env: 'stg',
  },
};

// https://docs.datadoghq.com/real_user_monitoring/browser/#npm
const RumConfiguration: RumInitConfiguration = {
  ...RumEnvConfig[app],
  site: 'datadoghq.com',
  version: env.version,
  sessionSampleRate: 100,
  trackLongTasks: true,
  trackResources: true,
  enableExperimentalFeatures: ['feature_flags'], // Enable feature flags for the RUM agent
  trackUserInteractions: false, // trackInteractions parameter enables the automatic collection of user clicks in the app, sensitive and private data can be included if it's true
  beforeSend: (event) => {
    const {
      type,
      view: { url, referrer },
    } = event;

    if (type === 'resource') {
      event.resource.url = PIIRedactor.fromURL(event.resource.url);
    }

    event.view.url = PIIRedactor.fromURL(url);

    event.view.referrer = referrer ? PIIRedactor.fromURL(referrer) : referrer;

    return true;
  },
};

// Skips calling the datadogRum service if environment or region information is missing (dev, testing, etc)
const wrapRUMFunc = <T extends Array<any>>(fn: (...args: T) => void) => {
  return (...args: T): void => {
    if (app && deployEnv) {
      fn(...args);
    }
  };
};

export const initializeRUM = wrapRUMFunc(() => datadogRum.init(RumConfiguration));

export const addUserContextToRUM = wrapRUMFunc((userContext: UserContext) =>
  datadogRum.setUser(userContext),
);

export const addTeamContextToRUM = wrapRUMFunc((teamContext: TeamContext) =>
  datadogRum.setGlobalContextProperty('team', teamContext),
);
